import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminRequestService, UserRequest, UpdatePhotosResponse } from 'app/services/adminrequest/adminrequest.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateProjetModalComponent } from '../create-projet-modal/create-projet-modal.component';
import Swal from 'sweetalert2';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-admin-request-detail',
  templateUrl: './admin-request-detail.component.html',
  styleUrls: ['./admin-request-detail.component.scss']
})
export class AdminRequestDetailComponent implements OnInit {
  request: UserRequest | null = null;
  loading = false;
  error = '';
  showRejectModal = false;
  rejectComment = '';
  downloadingAll = false;
  imagesDownloadedByAdmin = false;
  deletingMarked = false;

  downloadingVideo = false;
  videoDeletedProjet = false;
  videoDeletedGeneric = false;
  detectedVideoUrl: string | null = null;
  youtubeEmbedUrl: SafeResourceUrl | null = null;

  selectedImageForZoom: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminRequestService: AdminRequestService,
    private awsControlService: AwscontrolService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadRequest(+id);
    } else {
      this.error = 'Aucun identifiant de demande fourni.';
    }
  }

  loadRequest(id: number): void {
    this.loading = true;
    this.adminRequestService.getRequestById(id).subscribe({
      next: (data) => {
        this.request = data;
        this.loading = false;
        this.imagesDownloadedByAdmin = false;
        this.videoDeletedProjet = false;
        this.videoDeletedGeneric = false;
        this.detectedVideoUrl = this.findVideoUrl(this.request);
        
        if (this.detectedVideoUrl && this.isYoutubeUrl(this.detectedVideoUrl)) {
          this.youtubeEmbedUrl = this.getYoutubeEmbedUrl(this.detectedVideoUrl);
        } else {
          this.youtubeEmbedUrl = null;
        }
        
        if (!data.viewedByAdmin) {
          this.adminRequestService.markAsRead(data.id).subscribe();
        }
      },
      error: (err) => {
        this.error = err.message || 'Impossible de charger la demande.';
        this.loading = false;
      }
    });
  }

  private findVideoUrl(obj: any): string | null {
    if (!obj) return null;
    const videoFields = ['videoUrl', 'video_url', 'videoUrlProjet', 'video_url_projet', 'video', 'url', 'videoUrlProduit'];
    for (const field of videoFields) {
      const value = obj[field];
      if (value && typeof value === 'string' && (value.startsWith('http') || value.includes('s3'))) {
        return value;
      }
    }
    for (const key of Object.keys(obj)) {
      if (obj[key] && typeof obj[key] === 'object') {
        const nested = this.findVideoUrl(obj[key]);
        if (nested) return nested;
      }
    }
    return null;
  }

  isAWSVideoUrl(url: string | null): boolean {
    if (!url) return false;
    return url.includes('s3.amazonaws.com') || url.includes('tunisie-immob.s3');
  }

  isYoutubeUrl(url: string | null): boolean {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  getYoutubeEmbedUrl(url: string): SafeResourceUrl {
    let embedUrl = '';
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('/embed/')) {
      embedUrl = url;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  async downloadAndDeleteVideo(): Promise<void> {
    if (!this.detectedVideoUrl) {
      Swal.fire('Info', 'Aucune vidéo à télécharger.', 'info');
      return;
    }
    if (!this.isAWSVideoUrl(this.detectedVideoUrl)) {
      Swal.fire('Info', 'Cette vidéo n’est pas hébergée sur AWS, impossible de la supprimer automatiquement.', 'info');
      return;
    }

    this.downloadingVideo = true;
    Swal.fire({
      title: 'Téléchargement en cours...',
      text: 'Veuillez patienter...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const blob = await this.awsControlService.downloadFile(this.detectedVideoUrl).toPromise();
      const fileName = this.awsControlService.extractFileName(this.detectedVideoUrl) || 'video.mp4';
      saveAs(blob, fileName);
      Swal.close();

      await this.awsControlService.deleteFile(this.detectedVideoUrl).toPromise();

      this.detectedVideoUrl = null;
      this.youtubeEmbedUrl = null;
      if (this.isProjet()) {
        this.videoDeletedProjet = true;
      } else {
        this.videoDeletedGeneric = true;
      }
      Swal.fire('Succès', 'Vidéo téléchargée et supprimée du serveur.', 'success');
    } catch (err: any) {
      Swal.fire('Erreur', err.message || 'Impossible de traiter la vidéo.', 'error');
    } finally {
      this.downloadingVideo = false;
    }
  }

  async deleteVideoOnly(): Promise<void> {
    if (!this.detectedVideoUrl) {
      Swal.fire('Info', 'Aucune vidéo à supprimer.', 'info');
      return;
    }
    if (!this.isAWSVideoUrl(this.detectedVideoUrl)) {
      Swal.fire('Info', 'Cette vidéo n’est pas sur AWS, suppression impossible.', 'info');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Supprimer la vidéo ?',
      text: 'Cette action est irréversible. La vidéo sera effacée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });
    if (!confirm.isConfirmed) return;

    try {
      await this.awsControlService.deleteFile(this.detectedVideoUrl).toPromise();
      this.detectedVideoUrl = null;
      this.youtubeEmbedUrl = null;
      if (this.isProjet()) {
        this.videoDeletedProjet = true;
      } else {
        this.videoDeletedGeneric = true;
      }
      Swal.fire('Supprimée', 'La vidéo a été supprimée du serveur.', 'success');
    } catch (err: any) {
      Swal.fire('Erreur', err.message || 'Échec de la suppression.', 'error');
    }
  }

  approve(): void {
    if (!this.request) return;
    Swal.fire({
      title: 'Approuver la demande',
      text: `Êtes-vous sûr de vouloir approuver cette demande ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, approuver',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.adminRequestService.approveRequest(this.request!.id).subscribe({
          next: () => {
            Swal.fire('Approuvé!', 'La demande a été approuvée avec succès.', 'success');
            this.loadRequest(this.request!.id);
          },
          error: (err) => {
            Swal.fire('Erreur', err.message || 'Une erreur est survenue.', 'error');
          }
        });
      }
    });
  }

  openRejectModal(): void {
    this.rejectComment = '';
    this.showRejectModal = true;
  }

  confirmReject(): void {
    if (!this.request) return;
    if (!this.rejectComment.trim()) {
      Swal.fire('Commentaire requis', 'Veuillez fournir un commentaire pour justifier le rejet.', 'warning');
      return;
    }
    this.adminRequestService.rejectRequest(this.request.id, this.rejectComment).subscribe({
      next: () => {
        this.showRejectModal = false;
        Swal.fire('Rejeté', 'La demande a été rejetée.', 'success');
        this.loadRequest(this.request!.id);
      },
      error: (err) => {
        Swal.fire('Erreur', err.message || 'Une erreur est survenue.', 'error');
      }
    });
  }

  deleteRequest(): void {
    if (!this.request) return;
    Swal.fire({
      title: 'Supprimer la demande',
      text: `Cette action est irréversible. Voulez-vous vraiment supprimer cette demande ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.adminRequestService.deleteRequest(this.request!.id).subscribe({
          next: () => {
            Swal.fire('Supprimée!', 'La demande a été supprimée.', 'success');
            this.router.navigate(['/admin-requests']);
          },
          error: (err) => {
            Swal.fire('Erreur', err.message || 'Une erreur est survenue.', 'error');
          }
        });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin-requests']);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'APPROUVE': return 'badge-success';
      case 'REFUSE': return 'badge-danger';
      default: return 'badge-warning';
    }
  }

  getOperationLabel(operation: string): string {
    switch(operation) {
      case 'AJOUT': return '➕ Ajout';
      case 'MODIFICATION': return '✏️ Modification';
      case 'SUPPRESSION': return '🗑️ Suppression';
      default: return operation;
    }
  }

  isProfileUpdate(): boolean {
    return this.request?.requestType === 'PROFIL_UPDATE';
  }

  // ==================== IMAGES ====================
  private async deleteImagesFromAWS(): Promise<{ success: number; failed: number; errors: string[] }> {
    if (!this.request?.imageLinks?.length) return { success: 0, failed: 0, errors: [] };
    let success = 0, failed = 0;
    const errors: string[] = [];
    const promises = this.request.imageLinks.map(async (url, idx) => {
      try {
        await this.awsControlService.deleteFile(url).toPromise();
        success++;
      } catch (err: any) {
        failed++;
        errors.push(`Image ${idx+1}: ${err.message || 'Erreur inconnue'}`);
      }
    });
    await Promise.all(promises);
    return { success, failed, errors };
  }

  async downloadAllImages(): Promise<void> {
    if (!this.request?.imageLinks?.length) {
      Swal.fire('Info', 'Aucune image à télécharger.', 'info');
      return;
    }
    this.downloadingAll = true;
    const zip = new JSZip();
    let successCount = 0, errorCount = 0;
    Swal.fire({ title: 'Préparation...', text: 'Veuillez patienter...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const downloadPromises = this.request.imageLinks.map(async (url, index) => {
        try {
          const blob = await this.awsControlService.downloadFile(url).toPromise();
          const fileName = `image_${index + 1}_${this.awsControlService.extractFileName(url)}`;
          zip.file(fileName, blob);
          successCount++;
        } catch (err: any) {
          errorCount++;
        }
      });
      await Promise.all(downloadPromises);
      if (successCount === 0) throw new Error('Aucune image n’a pu être téléchargée.');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `images_demande_${this.request.id}.zip`);
      const deletionResult = await this.deleteImagesFromAWS();
      let dbUpdateSuccess = false;
      try {
        const updateResponse: UpdatePhotosResponse = await this.adminRequestService.updatePhotos(this.request.id, []).toPromise();
        dbUpdateSuccess = updateResponse.success;
      } catch (dbErr: any) {}
      this.request.imageLinks = [];
      this.imagesDownloadedByAdmin = true;
      let msg = `${successCount} image(s) téléchargée(s)<br>${deletionResult.success} supprimée(s) du serveur<br>`;
      if (!dbUpdateSuccess) msg += `<br>⚠️ Base de données non mise à jour.`;
      await Swal.fire({ title: 'Terminé', html: msg, icon: (successCount > 0 && deletionResult.success > 0 && dbUpdateSuccess) ? 'success' : 'warning' });
    } catch (error: any) {
      Swal.fire('Erreur', error.message || 'Impossible de créer l\'archive ZIP.', 'error');
    } finally {
      this.downloadingAll = false;
    }
  }

  async deleteMarkedImages(): Promise<void> {
    if (!this.request?.deletedImages?.length) {
      Swal.fire('Info', 'Aucune image marquée à supprimer.', 'info');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Supprimer les images marquées ?',
      text: `Vous allez supprimer définitivement ${this.request.deletedImages.length} image(s) du serveur. Cette action est irréversible.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });
    if (!confirm.isConfirmed) return;

    this.deletingMarked = true;
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < this.request.deletedImages.length; i++) {
      const url = this.request.deletedImages[i];
      try {
        await this.awsControlService.deleteFile(url).toPromise();
        successCount++;
      } catch (err: any) {
        failCount++;
      }
    }

    if (successCount > 0) {
      this.request.deletedImages = [];
      Swal.fire({
        title: 'Suppression terminée',
        html: `${successCount} image(s) supprimée(s) avec succès.${failCount > 0 ? `<br>${failCount} échec(s).` : ''}`,
        icon: successCount > 0 ? 'success' : 'error'
      });
      this.loadRequest(this.request.id);
    } else {
      Swal.fire('Erreur', 'Aucune image n’a pu être supprimée.', 'error');
    }
    this.deletingMarked = false;
  }

  openImageZoom(imageUrl: string): void {
    this.selectedImageForZoom = imageUrl;
  }

  closeImageZoom(): void {
    this.selectedImageForZoom = null;
  }

  // ==================== ENTITY PAGE NAVIGATION ====================
  openEntityPage(): void {
    if (!this.request) return;
    const type = this.request.requestType;
    const operation = this.request.operationType;
    const targetId = this.request.targetEntityId;
    let url = '';

    const openTab = (path: string) => {
      const base = window.location.origin;
      window.open(base + path, '_blank');
    };

    switch (type) {
      case 'PROJET':
        if (operation === 'AJOUT') url = '/ajouter-projet';
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-projet/${targetId}`;
        break;
      case 'PIECE':
        if (operation === 'AJOUT' && targetId) url = `/ajouter-pieces/${targetId}`;
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-piece/${targetId}`;
        break;
      case 'PLAN':
        if (operation === 'AJOUT' && targetId) url = `/piece/${targetId}/plans/add`;
        else if (operation === 'MODIFICATION' && targetId) url = `/piece/${targetId}/plans/edit/${targetId}`;
        break;
      case 'PRODUIT':
        if (operation === 'AJOUT') url = '/ajouter-produit';
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-produit/${targetId}`;
        break;
      case 'SOUS_PRODUIT':
        if (operation === 'AJOUT' && targetId) url = `/AjouterSousCat/${targetId}`;
        else if (operation === 'MODIFICATION' && targetId) url = `/modifierSousCat/${targetId}`;
        break;
      case 'SERVICE':
        if (operation === 'AJOUT') url = '/ajouter-Services';
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-Services/${targetId}`;
        break;
      case 'SOUSSERVICE':
        if (operation === 'AJOUT' && targetId) url = `/ajouter-sous-service/${targetId}`;
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-sous-service/${targetId}`;
        break;
      case 'BIEN':
        if (operation === 'AJOUT') url = '/ajouter-bien';
        else if (operation === 'MODIFICATION' && targetId) url = `/modifier-bien/${targetId}`;
        break;
      case 'SOUSBIEN':
        if (operation === 'AJOUT' && targetId) url = `/AjouterSousBien/${targetId}`;
        else if (operation === 'MODIFICATION' && targetId) url = `/modifierSousBien/${targetId}`;
        break;
      default:
        return;
    }

    if (url) openTab(url);
  }

  shouldShowEntityButton(): boolean {
    if (!this.request) return false;
    const type = this.request.requestType;
    return ['PROJET', 'PIECE', 'PLAN', 'PRODUIT', 'SOUS_PRODUIT', 'SERVICE', 'SOUSSERVICE', 'BIEN', 'SOUSBIEN'].includes(type);
  }

  isSuppression(): boolean { return this.request?.operationType === 'SUPPRESSION'; }
  isProjet(): boolean { return this.request?.requestType === 'PROJET'; }
  isPiece(): boolean { return this.request?.requestType === 'PIECE'; }
  isPlan(): boolean { return this.request?.requestType === 'PLAN'; }
  isProduit(): boolean { return this.request?.requestType === 'PRODUIT'; }
  isSousProduit(): boolean { return this.request?.requestType === 'SOUS_PRODUIT'; }
  isService(): boolean { return this.request?.requestType === 'SERVICE'; }
  isSousService(): boolean { return this.request?.requestType === 'SOUSSERVICE'; }
  isBien(): boolean { return this.request?.requestType === 'BIEN'; }
  isSousBien(): boolean { return this.request?.requestType === 'SOUSBIEN'; }

  openCreateProjetModal(): void {
    if (!this.request) return;
    const requestData = {
      agencyName: (this.request as any).agencyName || this.request.targetEntityName || '',
      name: (this.request as any).name || '',
      description: this.request.description || '',
      adresse: (this.request as any).adresse || '',
      videoUrlProjet: (this.request as any).videoUrlProjet || '',
      categoryList: (this.request as any).categoryList || [],
      promoTitle: (this.request as any).promoTitle || '',
      promoImg: (this.request as any).promoImg || '',
      nomCommercial: (this.request as any).nomCommercial || '',
      numCommercial: (this.request as any).numCommercial || '',
      emailCommercial: (this.request as any).emailCommercial || '',
      status: (this.request as any).status || 'active',
      date: (this.request as any).date || '',
      displayOrder: (this.request as any).displayOrder || 0,
      existingImageUrls: this.request.imageLinks || []
    };
    const dialogRef = this.dialog.open(CreateProjetModalComponent, { width: '800px', data: requestData });
    dialogRef.afterClosed().subscribe(result => { if (result) Swal.fire('Succès', 'Le projet a été créé.', 'success'); });
  }
}